"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { MotionConfig } from "motion/react";
import { api } from "@/data/api";
import type { User } from "@/data/types/user";
import type { Organization, OrganizationRole } from "@/data/types/organization";
import { OverlayProvider } from "@/components/overlay/overlay";
import { AuthModal } from "./auth-modal";
type AuthContextValue = {
  user: User | null;
  loading: boolean;
  organization: Organization | null;
  role: OrganizationRole | null;
  error: string;
  openAuth: (mode: "login" | "signup") => void;
  refresh: () => Promise<User | null>;
  logout: () => Promise<void>;
};
const AuthContext = createContext<AuthContextValue | null>(null);
export function AuthProvider(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [role, setRole] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(null);
  const sessionRequest = useRef(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async () => {
      const request = sessionRequest.current = sessionRequest.current + 1;
      const session = await api.organization.user.authentication.session.fetch();
      if (!session || !("valid" in session)) {
        if (request === sessionRequest.current) {
          setError("Atlas couldn\u2019t check your session. Please try again.");
          setOrganization(null);
          setRole(null);
        }
        throw new Error("Atlas couldn\u2019t check your session. Please try again.");
      }
      const current = session.valid ? session.user : null;
      if (request === sessionRequest.current) {
        setUser(current);
        setOrganization(session.valid ? session.organization ?? session.workspace : null);
        setRole(session.valid ? session.role : null);
        setError("");
      }
      return current;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const refresh = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      refresh().catch(_temp).finally(() => setLoading(false));
    };
    t3 = [refresh];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async function logout() {
      const result = await api.organization.user.authentication.logout.fetch();
      if (!result?.loggedOut) {
        throw new Error("Couldn\u2019t sign out. Please try again.");
      }
      sessionRequest.current = sessionRequest.current + 1;
      setUser(null);
      setOrganization(null);
      setRole(null);
      setError("");
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const logout = t4;
  let t5;
  if ($[4] !== error || $[5] !== loading || $[6] !== organization || $[7] !== role || $[8] !== user) {
    t5 = {
      user,
      loading,
      organization,
      role,
      error,
      openAuth: setMode,
      refresh,
      logout
    };
    $[4] = error;
    $[5] = loading;
    $[6] = organization;
    $[7] = role;
    $[8] = user;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children) {
    t6 = <div data-atlas-content={true}>{children}</div>;
    $[10] = children;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== mode) {
    t7 = mode && <AuthModal mode={mode} onClose={() => setMode(null)} />;
    $[12] = mode;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <MotionConfig reducedMotion="user"><OverlayProvider>{t6}{t7}</OverlayProvider></MotionConfig>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t8) {
    t9 = <AuthContext.Provider value={t5}>{t8}</AuthContext.Provider>;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function _temp() {}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return context;
}